import {
  SignupDTO,
  LoginDTO,
  SignUpResponseDO,
  LoginResponseDO,
  RefreshTokenResponseDO,
  SwitchBranchDTO,
  AdminViewExchangeResponseDO,
  PortalGymsResponseDO,
} from "../types/auth";
import axiosInstance from "@/lib/axiosInstance";
import { clearPortalAuthSnapshot } from "@/lib/authStorage";
import { clearTokens } from "../lib/tokenService";
import {
  createApiError,
  getApiErrorMessage,
  getApiFailureKind,
} from "@/lib/apiError";
import axios from "axios";
import { queryClient } from "@/lib/queryClient";

const BASE_URL = import.meta.env.VITE_API_BASE_URL;

interface MessageResponse {
  message: string;
}

interface VerifyForgotPasswordOtpResponse extends MessageResponse {
  resetToken: string;
  resetTokenExpiresAt: string;
}

// Auth service
export const authService = {
  async signup(userData: SignupDTO): Promise<SignUpResponseDO> {
    try {
      const response = await axiosInstance.post<SignUpResponseDO>(
        "/auth/signup",
        userData
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || "Signup failed");
      }
      throw new Error("An unexpected error occurred during signup");
    }
  },

  async login(
    credentials: LoginDTO,
    options?: { onRetry?: () => void },
  ): Promise<LoginResponseDO> {
    try {
      const response = await axiosInstance.post<LoginResponseDO>(
        "/auth/staff/login",
        credentials,
        {
          meta: { retryPolicy: "login", onRetry: options?.onRetry },
        } as never,
      );
      return response.data;
    } catch (error) {
      if (getApiFailureKind(error) === "transport" || getApiFailureKind(error) === "timeout") {
        throw new Error("Connection interrupted. Please try again.");
      }
      throw new Error(getApiErrorMessage(error, "Login failed"));
    }
  },

  async refreshToken(): Promise<RefreshTokenResponseDO> {
    try {
      const response = await axiosInstance.post<RefreshTokenResponseDO>(
        "/auth/refresh-token",
        {},
        { meta: { retryPolicy: "refresh", skipAuth: true } } as never,
      );
      return response.data;
    } catch (error) {
      throw createApiError(error, "Session expired");
    }
  },

  async switchBranch(payload: SwitchBranchDTO): Promise<LoginResponseDO> {
    try {
      const response = await axiosInstance.post<LoginResponseDO>(
        "/auth/switch-branch",
        payload
      );
      return response.data;
    } catch (error) {
      throw new Error(getApiErrorMessage(error, "Branch switch failed"));
    }
  },

  async fetchAdminViewGyms(): Promise<PortalGymsResponseDO> {
    try {
      const response = await axiosInstance.get<PortalGymsResponseDO>(
        "/auth/admin-view/gyms",
      );
      return response.data;
    } catch (error) {
      throw new Error(
        getApiErrorMessage(error, "Failed to fetch admin view branches"),
      );
    }
  },

  async switchAdminViewBranch(
    payload: SwitchBranchDTO,
  ): Promise<AdminViewExchangeResponseDO> {
    try {
      const response = await axiosInstance.post<AdminViewExchangeResponseDO>(
        "/auth/admin-view/switch-branch",
        payload,
      );
      return response.data;
    } catch (error) {
      throw new Error(
        getApiErrorMessage(error, "Admin view branch switch failed"),
      );
    }
  },

  async logout(): Promise<void> {
    try {
      await axiosInstance.post("/auth/logout");
    } catch (error) {
      // Even if logout fails, clear tokens locally
      console.error("Logout error:", error);
    } finally {
      // Clear tokens from storage
      clearPortalAuthSnapshot();
      clearTokens();
      queryClient.clear();
    }
  },

  async checkIfEmailExists(email: string) {
    try {
      const response = await axiosInstance.get<{
        exists: boolean;
        message?: string;
      }>(`/auth/check-email?email=${email}`);
      return response.data;
    } catch (error) {
      throw new Error(getApiErrorMessage(error, "Login failed"));
    }
  },

  async sendOTP(email: string): Promise<MessageResponse> {
    try {
      const response = await axiosInstance.post<MessageResponse>(
        "/auth/send-otp",
        { email }
      );
      return response.data;
    } catch (error) {
      throw new Error(getApiErrorMessage(error, "Send OTP failed"));
    }
  },

  async verifyOTP(email: string, otp: string): Promise<MessageResponse> {
    try {
      const response = await axiosInstance.post<MessageResponse>(
        "/auth/verify-otp",
        {
          email,
          otp,
        }
      );
      return response.data;
    } catch (error) {
      throw new Error(getApiErrorMessage(error, "Verify OTP failed"));
    }
  },

  async verifyForgotPasswordOTP(
    email: string,
    otp: string
  ): Promise<VerifyForgotPasswordOtpResponse> {
    try {
      const response =
        await axiosInstance.post<VerifyForgotPasswordOtpResponse>(
          "/auth/verify-forgot-password-otp",
          {
            email,
            otp,
          }
        );
      return response.data;
    } catch (error) {
      throw new Error(
        getApiErrorMessage(error, "Forgot password OTP verification failed")
      );
    }
  },

  async resetPassword(
    newPassword: string,
    resetToken: string
  ): Promise<MessageResponse> {
    try {
      const response = await axiosInstance.post<MessageResponse>(
        "/auth/update-password",
        {
          newPassword,
          resetToken,
        }
      );
      return response.data;
    } catch (error) {
      throw new Error(getApiErrorMessage(error, "Update Password failed"));
    }
  },

  async exchangeAdminViewToken(
    token: string,
  ): Promise<AdminViewExchangeResponseDO> {
    try {
      const response = await axios.post<AdminViewExchangeResponseDO>(
        `${BASE_URL}/auth/admin-view/exchange`,
        { token },
      );
      return response.data;
    } catch (error) {
      throw new Error(
        getApiErrorMessage(error, "Admin view session is invalid or expired"),
      );
    }
  },

  // // Check if user is authenticated
  // isAuthenticated(): boolean {
  //   return !!getAccessToken();
  // },
};
